import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import {
  buttonStyles,
  type ButtonSize,
  type ButtonVariant,
} from './buttonStyles';
import { Spinner } from './Spinner';
import { Tooltip } from './Tooltip';
import { useButtonState } from './useButtonState';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Icon before the label; replaced by a spinner while `loading`. */
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /** Shows a spinner, sets `aria-busy`, and ignores activation. */
  loading?: boolean;
  /**
   * Why the button is disabled. With `disabled`, the button stays focusable
   * (`aria-disabled`), ignores activation, shows the reason in a tooltip,
   * and exposes it as the accessible description.
   */
  disabledReason?: ReactNode;
}

/**
 * The app's only button. Use `primary` for the single next action on a
 * screen, `secondary` for other actions, `ghost` in toolbars and menus, and
 * `danger` only to confirm a destructive action.
 */
export const Button = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  ButtonProps
>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    loading = false,
    disabled,
    disabledReason,
    onClick,
    className,
    children,
    type = 'button',
    'aria-describedby': describedBy,
    ...props
  },
  ref
) {
  const { hasReason, reasonId, styleState, buttonProps } = useButtonState({
    disabled,
    disabledReason,
    loading,
    onClick,
    describedBy,
  });

  const button = (
    <button
      ref={ref}
      type={type}
      className={buttonStyles({ variant, size, ...styleState, className })}
      {...props}
      {...buttonProps}
    >
      {loading ? <Spinner size={size === 'sm' ? 'sm' : 'md'} /> : leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );

  if (!hasReason) return button;
  return (
    <>
      <Tooltip content={disabledReason}>{button}</Tooltip>
      <span id={reasonId} className="sr-only">
        {disabledReason}
      </span>
    </>
  );
});
